import React, { useContext, useState } from "react";
import Image from "next/image";
import { SbEditableBlok, sbEditable } from "@storyblok/storyblok-editable";
import { RichTextV2, RichTextV2Props } from "components/V2/atoms/RichTextV2/RichTextV2";
import { screenContext } from "context/ScreenContext";
import { AccordionV2 } from "../../atoms/AccordionV2/AccordionV2";
import { AppButtonV2, AppButtonV2Props } from "../../atoms/AppButtonV2/AppButtonV2";
import { ButtonV2, ButtonV2Props } from "../../atoms/ButtonV2/ButtonV2";
import { ImageV2, ImageV2Props } from "../../atoms/ImageV2/ImageV2";
import { NavLinkV2, NavLinkV2Props } from "../../atoms/NavLinkV2/NavLinkV2";
import { SocialIconV2, SocialIconV2Props } from "../../atoms/SocialIconV2/SocialIconV2";
import { FooterLinksBlockV2 } from "./FooterLinksBlockV2/FooterLinksBlockV2";

type FooterSection = SbEditableBlok & {
  label: string;
  links: NavLinkV2Props["blok"][];
};
export interface FooterV2Props {
  blok: {
    linksSection: FooterSection[];
    termsAndConditionsCTAs: ButtonV2Props["blok"][];
    logo: ImageV2Props["blok"][];
    downloadAppCTA: AppButtonV2Props["blok"][];
    downloadAppLabel: string;
    socialIcons: SocialIconV2Props["blok"][];
    copyRightContent: RichTextV2Props["blok"][];
    _uid: string;
    component: string;
  };
  removeFooter?: boolean;
  removeMobFooter?: boolean;
}

export function FooterV2({
  blok: {
    linksSection,
    termsAndConditionsCTAs,
    logo: logoBlokArray,
    downloadAppCTA,
    downloadAppLabel,
    socialIcons,
    copyRightContent: copyRightContentBlokArray,
    ...blokData
  },
  removeFooter,
  removeMobFooter
}: FooterV2Props) {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [logo] = logoBlokArray || [];
  const [copyRightContent] = copyRightContentBlokArray || [];
  const { currentDevice } = useContext(screenContext);

  return (
    <footer className={`w-full my-0 lg:mt-24 lg:px-4 ${removeFooter && "block sm:hidden"} ${removeMobFooter && "hidden sm:block"}`} aria-label="Footer Menu" {...sbEditable(blokData)}>
      <div className="mx-auto max-w-[1140px]">
        <div className="hidden lg:grid lg:gap-2 grid-cols-4 grid-rows-1 ">
          {linksSection?.length > 0 &&
            linksSection.map((footerItem: any) => (
              <h2 className="font-medium text-lg" key={footerItem?.label}>
                {footerItem.label}
              </h2>
            ))}
        </div>
        <div aria-label="footer-divider" className="hidden lg:flex justify-center items-center py-8 mx-auto relative">
          <hr className="w-3/4 h-1-r mx-auto dark:border-gray-600" />
          <button
            type="button"
            className={`absolute transform items-center transition-transform duration-700 ${
              isOpen ? "rotate-180" : ""
            }`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <Image src="/icons/dropdown-arrow.svg" width={100} height={30} alt="dropdown-arrow" />
          </button>
        </div>
        <div className={`max-w-[1140px] mx-auto ${isOpen ? "hidden lg:grid grid-cols-4 grid-rows-1" : "hidden"}`}>
          {linksSection?.length > 0 &&
            linksSection.map((footerItem: any) => <FooterLinksBlockV2 blok={footerItem} key={footerItem._uid} />)}
        </div>

        {linksSection?.length > 0 &&
          linksSection.map((nestedBlok: any, index: number) => (
            <div key={nestedBlok._uid}> 
              <div className={`lg:hidden px-2 bg-[var(--color-gray-50)] relative ${
                index !== linksSection.length - 1 ? 
                "before:absolute before:bottom-0 before:left-4 before:right-4 before:h-[1px] before:bg-[#EAE1DE]" : 
                ""
              }`}>
                <AccordionV2 title={nestedBlok?.label}>
                  {nestedBlok?.links.map((linkItem: any) => (
                    <div className="py-2" key={linkItem?._uid}>
                      <NavLinkV2 blok={linkItem} />
                    </div>
                  ))}
                </AccordionV2>
              </div>
            </div>
          ))}
        <div className="flex justify-center lg:block mb-20 lg:mb-0">
          <div
            className=" hidden lg:flex flex-col space-y-6 lg:space-y-0 lg:flex-row lg:justify-between items-center my-6 lg:my-14 "
            aria-label="Copyright Banner"
          >
            <span className="mx-auto lg:mx-0 dark:invert dark:brightness-0">{logo && <ImageV2 blok={logo} />}</span>
            <span className="text-gray-500" aria-label="Copy Right">
              {copyRightContent && <RichTextV2 blok={copyRightContent} />}
            </span>
            <span className="flex justify-center">
              {termsAndConditionsCTAs?.length > 0 &&
                termsAndConditionsCTAs.map((nestedBlok, index: number) => {
                  const isLastItem = index + 1 !== termsAndConditionsCTAs?.length;
                  return (
                    <span key={nestedBlok?._uid} className="flex items-center">
                      <a
                        href={
                          nestedBlok?.link?.cached_url?.includes("/en")
                            ? nestedBlok?.link?.cached_url.slice(3)
                            : nestedBlok?.link?.cached_url
                        }
                      >
                        <ButtonV2 blok={nestedBlok} />
                      </a>
                      {isLastItem && <hr className="w-4 border-gray-100 border-[1px] rotate-90" />}
                    </span>
                  );
                })}
            </span>
          </div>

          <div
            className="flex justify-center flex-col w-full space-y-4 mt-6 sm:my-6 lg:space-y-0 lg:flex-row lg:justify-between items-center "
            aria-label="Social Media Banner"
          >
            {currentDevice === "mobile" ? (
              <div className="flex gap-4">
                <div className="flex-none font-medium text-[#666666] mt-2">{downloadAppLabel}</div>
                <div className="flex-none">
                  <a href={downloadAppCTA[0].ctaImages[0].isImageWithLink && downloadAppCTA[0].ctaImages[0].imageLink?.url} aria-label={downloadAppCTA[0].ctaImages[0].mobileImage?.alt || ""} target={downloadAppCTA[0].ctaImages[0].isLinkOpenInNewTab ? "_blank" : "_self"}>
                    <img src={downloadAppCTA[0].ctaImages[0].mobileImage?.filename} alt="Download app" />
                  </a>
                </div>
                <div className="h-10 w-px bg-gray-300" />
                <div className="flex-1">
                  <a href={downloadAppCTA[1].ctaImages[0].isImageWithLink && downloadAppCTA[1].ctaImages[0].imageLink?.url} aria-label={downloadAppCTA[1].ctaImages[0].mobileImage?.alt || ""} target={downloadAppCTA[1].ctaImages[0].isLinkOpenInNewTab ? "_blank" : "_self"}>
                    <img src={downloadAppCTA[1].ctaImages[0].mobileImage?.filename} alt="Download app" />
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex flex-col lg:flex-row items-center gap-2 mb-4 lg:mb-0">
                <h3 className="mb-4 lg:mb-0 font-medium whitespace-nowrap">{downloadAppLabel}</h3>
                <div className="flex gap-2">
                  {downloadAppCTA?.length > 0 &&
                    downloadAppCTA.map((nestedBlok) => <AppButtonV2 key={nestedBlok._uid} blok={nestedBlok} />)}
                </div>
              </div>
            )}
            <div>
              {currentDevice === "mobile" && blokData?.paymentCardsImage && (
                <img src={blokData?.paymentCardsImage[0]?.image?.filename} className="mb-5" alt={blokData?.paymentCardsImage[0]?.image?.meta_data?.alt || ""} />
              )}
            </div>

            {socialIcons?.length > 0 && currentDevice === "web" && (
              <div className="flex gap-6" aria-label="Connect with us on Social Media ">
                {socialIcons.map((nestedBlok: any) => (
                  <SocialIconV2 key={nestedBlok._uid} blok={nestedBlok} />
                ))}
              </div>
            )}

            {currentDevice === "mobile" && (
              <div className="bg-primary w-full pt-6">
                {socialIcons?.length > 0 && (
                  <div className="flex justify-center dark:invert gap-2 pb-4" aria-label="Connect with us on Social Media ">
                    {socialIcons.map((nestedBlok: any) => (
                      <SocialIconV2 key={nestedBlok._uid} blok={nestedBlok} />
                    ))}
                  </div>
                )}
                <div className="flex justify-center">
                  {termsAndConditionsCTAs?.length > 0 &&
                    termsAndConditionsCTAs.map((nestedBlok, index: number) => {
                      const isLastItem = index + 1 !== termsAndConditionsCTAs?.length;
                      return (
                        <div key={nestedBlok?._uid} className="flex items-center">
                          <a
                            href={
                              nestedBlok?.link?.cached_url?.includes("/en")
                                ? nestedBlok?.link?.cached_url.slice(3)
                                : nestedBlok?.link?.cached_url
                            }
                          >
                            <ButtonV2
                              blok={nestedBlok}
                              additionalButtonClassName="text-white dark:text-gray-600 text-lg font-medium bg-primary"
                            />
                          </a>
                          {isLastItem && <hr className="w-4 border-gray-100 border-[1px] rotate-90" />}
                        </div>
                      );
                    })}
                </div>

                <div className="text-gray-500" aria-label="Copy Right">
                  {copyRightContent && (
                    <RichTextV2
                      blok={copyRightContent}
                      additionalContentClasses="text-white dark:text-gray-600 text-center font-medium !text-xs pb-4 pt-3"
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default FooterV2;